// StudentAnalytics - Parent view of child's comprehensive analytics
import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { api, Roadmap } from "@/lib/api";
import {
  TrendingUp,
  Target,
  Clock,
  Award,
  BarChart3,
  Brain,
  AlertCircle,
  CheckCircle,
  Zap,
  ArrowLeft,
  Trophy,
  Flame,
  Medal,
  History,
  BookOpen,
  Map,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface ChildAnalyticsData {
  child_id: string;
  child_info: {
    id: string;
    username: string;
    first_name?: string;
    last_name?: string;
  };
  performance_summary: {
    total_sessions: number;
    total_problems_attempted: number;
    total_problems_correct: number;
    overall_accuracy: number;
    total_time_spent: number;
    avg_time_per_problem: number;
    unique_techniques_practiced: number;
    sessions_last_7_days: number;
    accuracy_last_7_days: number;
    best_session_correct: number;
    best_session_accuracy: number;
    unique_practice_days: number;
  };
  practice_sessions: Array<{
    id: string;
    mode: string;
    problems_attempted: number;
    problems_correct: number;
    total_time: number;
    started_at: string;
    completed_at: string;
    techniques?: { name: string; category: string; difficulty: string };
  }>;
  technique_mastery: Array<{
    technique_id: string;
    mastery_level: number;
    techniques?: { name: string; category: string; difficulty: string };
  }>;
  mastery_distribution: {
    novice: number;
    beginner: number;
    intermediate: number;
    advanced: number;
    expert: number;
  };
  weak_areas: Array<{
    technique_name: string;
    failure_rate: number;
    priority_score: number;
    recent_attempts: number;
  }>;
  streak: {
    current_streak: number;
    longest_streak: number;
  };
  badges: Array<{
    badge_id: string;
    earned_at: string;
    badges?: { name: string; description: string; icon: string };
  }>;
  ranking: {
    rank: number;
    total_students: number;
    percentile: number;
    rank_tier: string;
    rank_description: string;
    score: number;
    stats?: {
      total_problems_solved: number;
      accuracy: number;
      current_streak: number;
      longest_streak: number;
    };
  };
  learning_insights: Array<{
    id: string;
    title: string;
    description: string;
    priority: string;
    action_text?: string;
  }>;
}

export default function StudentAnalytics() {
  const { childId } = useParams<{ childId: string }>();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState<ChildAnalyticsData | null>(null);
  const [roadmaps, setRoadmaps] = useState<Roadmap[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (childId) {
      fetchChildAnalytics();
      fetchRoadmapsProgress();
    }
  }, [childId]);

  const fetchChildAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);

      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) {
        navigate("/login");
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/child-analytics?child_id=${childId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to fetch analytics");
      }

      const data = await response.json();
      setAnalytics(data);
    } catch (err) {
      console.error("Error fetching child analytics:", err);
      setError(err instanceof Error ? err.message : "Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  const fetchRoadmapsProgress = async () => {
    try {
      // Fetch roadmaps with progress for the child
      const roadmapsData = await api.getRoadmaps(true);
      setRoadmaps(roadmapsData);
    } catch (err) {
      console.error("Error fetching roadmaps:", err);
    }
  };

  const getRankTierColor = (tier: string) => {
    switch (tier) {
      case "legendary":
        return "text-yellow-500 bg-yellow-500/10 border-yellow-500";
      case "elite":
        return "text-purple-500 bg-purple-500/10 border-purple-500";
      case "master":
        return "text-red-500 bg-red-500/10 border-red-500";
      case "expert":
        return "text-orange-500 bg-orange-500/10 border-orange-500";
      case "proficient":
        return "text-blue-500 bg-blue-500/10 border-blue-500";
      case "intermediate":
        return "text-green-500 bg-green-500/10 border-green-500";
      case "beginner":
        return "text-teal-500 bg-teal-500/10 border-teal-500";
      default:
        return "text-gray-500 bg-gray-500/10 border-gray-500";
    }
  };

  const getMasteryLevelColor = (level: string) => {
    switch (level) {
      case "expert":
        return "bg-purple-600";
      case "advanced":
        return "bg-blue-600";
      case "intermediate":
        return "bg-green-600";
      case "beginner":
        return "bg-yellow-600";
      default:
        return "bg-gray-400";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl text-gray-600 dark:text-gray-400">
          Loading analytics...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4">
        <div className="text-xl text-red-600 dark:text-red-400">{error}</div>
        <Button onClick={() => navigate("/parent-portal")}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Parent Portal
        </Button>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl text-gray-600 dark:text-gray-400">
          No analytics data available
        </div>
      </div>
    );
  }

  const summary = analytics.performance_summary;
  const childName =
    analytics.child_info?.first_name ||
    analytics.child_info?.username ||
    "Student";

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link to="/parent-portal">
            <Button variant="ghost" className="mb-4">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Parent Portal
            </Button>
          </Link>
          <div className="flex items-center gap-3 mb-2">
            <BarChart3 className="w-8 h-8 text-blue-600" />
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white">
              {childName}'s Analytics
            </h1>
          </div>
          <p className="text-gray-600 dark:text-gray-400">
            Comprehensive learning insights and progress tracking
          </p>
        </div>

        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-8">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="mastery">Mastery & Progress</TabsTrigger>
            <TabsTrigger value="history">Practice History</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-8">
            {/* Ranking Card - HackerRank Style */}
            {analytics.ranking && (
              <Card
                className={`border-2 ${getRankTierColor(analytics.ranking.rank_tier)}`}
              >
                <CardContent className="py-8">
                  <div className="text-center">
                    <Trophy
                      className={`w-12 h-12 mx-auto mb-4 ${
                        analytics.ranking.rank_tier === "legendary"
                          ? "text-yellow-500"
                          : analytics.ranking.rank_tier === "elite"
                            ? "text-purple-500"
                            : analytics.ranking.rank_tier === "master"
                              ? "text-red-500"
                              : "text-blue-500"
                      }`}
                    />
                    <div className="text-xl text-gray-600 dark:text-gray-400 mb-2">
                      {childName} is in the
                    </div>
                    <div
                      className={`text-5xl md:text-6xl font-bold mb-4 ${
                        analytics.ranking.rank_tier === "legendary"
                          ? "text-yellow-500"
                          : analytics.ranking.rank_tier === "elite"
                            ? "text-purple-500"
                            : analytics.ranking.rank_tier === "master"
                              ? "text-red-500"
                              : analytics.ranking.rank_tier === "expert"
                                ? "text-orange-500"
                                : analytics.ranking.rank_tier === "proficient"
                                  ? "text-blue-500"
                                  : "text-green-500"
                      }`}
                    >
                      {analytics.ranking.rank_description}
                    </div>
                    <div className="flex items-center justify-center gap-4 text-gray-500 dark:text-gray-400">
                      <span>
                        Rank #{analytics.ranking.rank.toLocaleString()}
                      </span>
                      <span>•</span>
                      <span>
                        {analytics.ranking.total_students.toLocaleString()}{" "}
                        students
                      </span>
                      <span>•</span>
                      <span>Top {analytics.ranking.percentile}%</span>
                    </div>
                    <div
                      className={`inline-block mt-4 px-6 py-2 rounded-full border-2 ${getRankTierColor(analytics.ranking.rank_tier)}`}
                    >
                      <span className="text-lg font-bold capitalize">
                        {analytics.ranking.rank_tier} Tier
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                        Total Sessions
                      </p>
                      <p className="text-3xl font-bold text-gray-900 dark:text-white">
                        {summary.total_sessions || 0}
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                      <Target className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                        Overall Accuracy
                      </p>
                      <p className="text-3xl font-bold text-gray-900 dark:text-white">
                        {summary.overall_accuracy?.toFixed(1) || 0}%
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                      <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                        Problems Solved
                      </p>
                      <p className="text-3xl font-bold text-gray-900 dark:text-white">
                        {summary.total_problems_correct || 0}
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                      <Zap className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                        Avg Time/Problem
                      </p>
                      <p className="text-3xl font-bold text-gray-900 dark:text-white">
                        {summary.avg_time_per_problem?.toFixed(1) || 0}s
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center">
                      <Clock className="w-6 h-6 text-orange-600 dark:text-orange-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Streak Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Flame className="w-5 h-5 text-orange-600" />
                  Practice Streak
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-8">
                  <div className="text-center">
                    <div className="text-4xl font-bold text-orange-600">
                      {analytics.streak?.current_streak || 0}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      Current Streak (days)
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-4xl font-bold text-purple-600">
                      {analytics.streak?.longest_streak || 0}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      Longest Streak (days)
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Recent Progress */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-green-600" />
                  Recent Progress (Last 7 Days)
                </CardTitle>
                <CardDescription>Performance trend</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                      Sessions
                    </p>
                    <p className="text-3xl font-bold text-gray-900 dark:text-white">
                      {summary.sessions_last_7_days || 0}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                      Accuracy
                    </p>
                    <p className="text-3xl font-bold text-gray-900 dark:text-white">
                      {summary.accuracy_last_7_days?.toFixed(1) || 0}%
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                      Best Session
                    </p>
                    <p className="text-3xl font-bold text-gray-900 dark:text-white">
                      {summary.best_session_correct || 0}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                      Practice Days
                    </p>
                    <p className="text-3xl font-bold text-gray-900 dark:text-white">
                      {summary.unique_practice_days || 0}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Learning Insights */}
            {analytics.learning_insights &&
              analytics.learning_insights.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Brain className="w-5 h-5" />
                      Learning Insights
                    </CardTitle>
                    <CardDescription>
                      Personalized recommendations
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {analytics.learning_insights.map((insight) => (
                        <div
                          key={insight.id}
                          className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-400 dark:hover:border-blue-600 transition-colors"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h4 className="font-semibold text-gray-900 dark:text-white mb-1">
                                {insight.title}
                              </h4>
                              <p className="text-sm text-gray-600 dark:text-gray-400">
                                {insight.description}
                              </p>
                            </div>
                            <span
                              className={`px-2 py-1 rounded text-xs font-semibold ${
                                insight.priority === "high"
                                  ? "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
                                  : insight.priority === "medium"
                                    ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400"
                                    : "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400"
                              }`}
                            >
                              {insight.priority}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
          </TabsContent>

          {/* Mastery & Progress Tab */}
          <TabsContent value="mastery" className="space-y-8">
            {/* Roadmap Progress */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Map className="w-5 h-5 text-blue-600" />
                  Learning Path Progress
                </CardTitle>
                <CardDescription>
                  Progress across different learning roadmaps
                </CardDescription>
              </CardHeader>
              <CardContent>
                {roadmaps.length > 0 ? (
                  <div className="space-y-6">
                    {roadmaps.map((roadmap) => {
                      const totalModules = roadmap.module_count || 0;
                      const completedModules =
                        roadmap.modules?.filter(
                          (module) =>
                            module.progress &&
                            module.progress.completed_lessons ===
                              module.progress.total_lessons &&
                            module.progress.total_lessons > 0,
                        ).length || 0;
                      const inProgressModules =
                        roadmap.modules?.filter(
                          (module) =>
                            module.progress &&
                            module.progress.in_progress_lessons > 0,
                        ).length || 0;

                      const progressPercentage =
                        totalModules > 0
                          ? (completedModules / totalModules) * 100
                          : 0;

                      return (
                        <div
                          key={roadmap.id}
                          className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-400 dark:hover:border-blue-600 transition-colors"
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1">
                              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                                {roadmap.icon} {roadmap.name}
                              </h3>
                              <p className="text-sm text-gray-600 dark:text-gray-400">
                                {roadmap.description}
                              </p>
                            </div>
                            <div className="text-right ml-4">
                              <div className="text-2xl font-bold text-blue-600">
                                {progressPercentage.toFixed(0)}%
                              </div>
                              <div className="text-xs text-gray-500">
                                Complete
                              </div>
                            </div>
                          </div>

                          {/* Progress Bar */}
                          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 mb-3">
                            <div
                              className="h-full rounded-full bg-gradient-to-r from-blue-500 to-blue-600"
                              style={{ width: `${progressPercentage}%` }}
                            />
                          </div>

                          {/* Module Stats */}
                          <div className="grid grid-cols-3 gap-4 text-sm">
                            <div className="text-center">
                              <div className="font-bold text-green-600 dark:text-green-400">
                                {completedModules}
                              </div>
                              <div className="text-xs text-gray-600 dark:text-gray-400">
                                Completed
                              </div>
                            </div>
                            <div className="text-center">
                              <div className="font-bold text-yellow-600 dark:text-yellow-400">
                                {inProgressModules}
                              </div>
                              <div className="text-xs text-gray-600 dark:text-gray-400">
                                In Progress
                              </div>
                            </div>
                            <div className="text-center">
                              <div className="font-bold text-gray-600 dark:text-gray-400">
                                {totalModules}
                              </div>
                              <div className="text-xs text-gray-600 dark:text-gray-400">
                                Total Modules
                              </div>
                            </div>
                          </div>

                          {/* Module Details */}
                          {roadmap.modules && roadmap.modules.length > 0 && (
                            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                              <div className="space-y-2">
                                {roadmap.modules.slice(0, 5).map((module) => {
                                  const moduleProgress = module.progress;
                                  const isCompleted =
                                    moduleProgress &&
                                    moduleProgress.completed_lessons ===
                                      moduleProgress.total_lessons &&
                                    moduleProgress.total_lessons > 0;
                                  const isInProgress =
                                    moduleProgress &&
                                    moduleProgress.in_progress_lessons > 0;

                                  return (
                                    <div
                                      key={module.id}
                                      className="flex items-center justify-between py-2"
                                    >
                                      <div className="flex items-center gap-2 flex-1">
                                        {isCompleted ? (
                                          <CheckCircle className="w-4 h-4 text-green-600" />
                                        ) : isInProgress ? (
                                          <Clock className="w-4 h-4 text-yellow-600" />
                                        ) : (
                                          <BookOpen className="w-4 h-4 text-gray-400" />
                                        )}
                                        <span className="text-sm text-gray-700 dark:text-gray-300">
                                          {module.icon} {module.name}
                                        </span>
                                      </div>
                                      {moduleProgress && (
                                        <span className="text-xs text-gray-500">
                                          {moduleProgress.completed_lessons}/
                                          {moduleProgress.total_lessons} lessons
                                        </span>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    No learning paths started yet
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Mastery Distribution */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Award className="w-5 h-5 text-yellow-600" />
                  Overall Technique Mastery
                </CardTitle>
                <CardDescription>
                  Skill level distribution across all techniques
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {Object.entries(analytics.mastery_distribution).map(
                    ([level, count]) => {
                      const total = Object.values(
                        analytics.mastery_distribution,
                      ).reduce((a, b) => a + b, 0);
                      const percentage = total > 0 ? (count / total) * 100 : 0;

                      return (
                        <div key={level}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium capitalize text-gray-700 dark:text-gray-300">
                              {level}
                            </span>
                            <span className="text-sm font-bold text-gray-900 dark:text-white">
                              {count} techniques
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                            <div
                              className={`h-full rounded-full ${getMasteryLevelColor(level)}`}
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                        </div>
                      );
                    },
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Weak Areas */}
            {analytics.weak_areas && analytics.weak_areas.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-orange-600" />
                    Areas for Improvement
                  </CardTitle>
                  <CardDescription>
                    Techniques that need more practice
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {analytics.weak_areas.map((area, idx) => (
                      <div
                        key={idx}
                        className="p-3 bg-orange-50 dark:bg-orange-900/10 border border-orange-200 dark:border-orange-800 rounded-lg"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-semibold text-gray-900 dark:text-white">
                            {area.technique_name}
                          </h4>
                          <span className="text-sm font-medium text-orange-600 dark:text-orange-400">
                            Priority: {area.priority_score?.toFixed(1)}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-gray-600 dark:text-gray-400">
                              Failure Rate:
                            </span>
                            <span className="ml-2 font-medium text-orange-600 dark:text-orange-400">
                              {area.failure_rate?.toFixed(1)}%
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-600 dark:text-gray-400">
                              Attempts:
                            </span>
                            <span className="ml-2 font-medium">
                              {area.recent_attempts}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Badges */}
            {analytics.badges && analytics.badges.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Medal className="w-5 h-5 text-yellow-600" />
                    Earned Badges
                  </CardTitle>
                  <CardDescription>Achievements unlocked</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {analytics.badges.map((badge) => (
                      <div
                        key={badge.badge_id}
                        className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg text-center"
                      >
                        <div className="text-3xl mb-2">
                          {badge.badges?.icon || "🏆"}
                        </div>
                        <div className="font-semibold text-sm">
                          {badge.badges?.name || "Badge"}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {new Date(badge.earned_at).toLocaleDateString()}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Practice History Tab */}
          <TabsContent value="history" className="space-y-8">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <History className="w-5 h-5 text-blue-600" />
                  Practice Session History
                </CardTitle>
                <CardDescription>
                  Recent practice sessions (last 50)
                </CardDescription>
              </CardHeader>
              <CardContent>
                {analytics.practice_sessions &&
                analytics.practice_sessions.length > 0 ? (
                  <div className="space-y-3">
                    {analytics.practice_sessions.map((session) => {
                      const accuracy =
                        session.problems_attempted > 0
                          ? (session.problems_correct /
                              session.problems_attempted) *
                            100
                          : 0;
                      const techniqueName =
                        session.techniques?.name ||
                        session.mode.replace(/_/g, " ");

                      return (
                        <div
                          key={session.id}
                          className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-400 dark:hover:border-blue-600 transition-colors"
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="font-semibold text-gray-900 dark:text-white capitalize">
                                {techniqueName}
                              </h4>
                              <p className="text-sm text-gray-500 mt-1">
                                {session.techniques?.category?.replace(
                                  /_/g,
                                  " ",
                                )}{" "}
                                | {session.techniques?.difficulty}
                              </p>
                            </div>
                            <div className="text-right">
                              <div
                                className={`text-xl font-bold ${
                                  accuracy >= 80
                                    ? "text-green-600"
                                    : accuracy >= 60
                                      ? "text-yellow-600"
                                      : "text-red-600"
                                }`}
                              >
                                {accuracy.toFixed(0)}%
                              </div>
                              <p className="text-sm text-gray-500">
                                {session.problems_correct}/
                                {session.problems_attempted} correct
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center justify-between mt-3 text-sm text-gray-500">
                            <span>
                              <Clock className="w-3 h-3 inline mr-1" />
                              {Math.round(session.total_time)}s total
                            </span>
                            <span>
                              {new Date(session.started_at).toLocaleString()}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    No practice sessions yet
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
